import { useReduxState } from 'hooks';
import { Owners } from 'components/common';
import { ErrorPage } from 'components/error';
import {
  Page,
  PageContent,
  PageHeader,
  PageInnerContent,
  PageSidebar,
  PageSidebarTitle
} from 'components/page';

interface Props extends React.PropsWithChildren {
  navActiveLink?: string;
}

const GroupPage = ({ children, navActiveLink }: Props) => {
  const {
    messages,
    pageState: { group },
    serverConfig,
    user
  } = useReduxState();

  const coursesVisible = !serverConfig.hideCoursesFromUI;

  if (!group) {
    return (
      <ErrorPage
        description={messages.errorPermissionMaybe}
        icon="users"
        title={messages.commonGroupNotFound}
      />
    );
  }

  const isOwner =
    user?.roles?.includes('superadmin') ||
    group.owners.some((owner) => owner.id === user?.id);

  const adminPermissions =
    group.admins.find((admin) => admin.id === user?.id)?.permissions || [];

  const links = [];

  if (isOwner || adminPermissions.includes('manage-users')) {
    links.push({
      href: `/groups/${group.id}`,
      icon: 'user',
      title: messages.commonUsers
    });
  }

  if (
    (isOwner || adminPermissions.includes('manage-courses')) &&
    coursesVisible
  ) {
    links.push({
      countItems: group.numberOfCourses,
      href: `/groups/${group.id}/courses`,
      icon: 'book',
      title: messages.commonCourses
    });
  }

  if (
    (isOwner || adminPermissions.includes('view-analytics')) &&
    coursesVisible
  ) {
    links.push({
      href: `/groups/${group.id}/follow`,
      icon: 'chart-line',
      title: messages.commonFollow
    });
  }

  if (isOwner) {
    links.push({
      href: `/groups/${group.id}/admins`,
      icon: 'user-shield',
      title: messages.commonAdministrators
    });
    links.push({
      href: `/groups/${group.id}/manage`,
      icon: 'tools',
      title: messages.commonSettings
    });
  }

  return (
    <Page activePathname="/groups">
      <PageHeader
        backLinkAs="/groups"
        backLinkHref="/groups"
        title={messages.commonGroups}
      />
      <PageContent withSidebar={true}>
        <PageSidebar
          active={`/groups/${group.id}${navActiveLink || ''}`}
          isWide={true}
          links={links}
        >
          <PageSidebarTitle
            icon="users"
            subtitle={<Owners owners={group.owners} />}
            title={group.title}
          />
        </PageSidebar>
        <PageInnerContent>{children}</PageInnerContent>
      </PageContent>
    </Page>
  );
};

export default GroupPage;
